import React, { useEffect, useState } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { getConsultantSettlements } from "../../services/api/consultantsPayout";

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * SettlementHistoryDialog — read-only list of past settlements for a single
 * consultant. Separated from the page so the consultants table stays
 * isolated from this dialog's data fetching + state.
 */
const SettlementHistoryDialog = ({ open, consultant, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !consultant?.id) return;
    setLoading(true);
    setError(false);
    getConsultantSettlements(consultant.id)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res?.data?.data?.data || []);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [open, consultant?.id]);

  const total = rows.reduce((acc, r) => acc + Number(r.amount || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Settlement History — {consultant?.name || consultant?.id || "consultant"}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div style={{ padding: "40px 0", textAlign: "center" }}>
            <CircularProgress size={26} />
          </div>
        ) : error ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#dc2626" }}>
            Failed to load settlements.
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280" }}>
            No settlements recorded yet for this consultant.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                background: "#f3f4f6",
                padding: "8px 12px",
                borderRadius: 6,
                marginBottom: 12,
                fontSize: "0.88rem",
              }}>
              <span>
                <b>{rows.length}</b> settlement{rows.length > 1 ? "s" : ""}
              </span>
              <span>
                Total paid: <b style={{ color: "#16a34a" }}>{inr(total)}</b>
              </span>
            </div>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance Before</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                  <TableCell>Reference / UTR</TableCell>
                  <TableCell>Bank</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id || r.SK}>
                    <TableCell>{formatDate(r.created_at)}</TableCell>
                    <TableCell align="right">
                      <b style={{ color: "#16a34a" }}>{inr(r.amount)}</b>
                    </TableCell>
                    <TableCell align="right">{inr(r.balance_before)}</TableCell>
                    <TableCell align="right">{inr(r.balance_after)}</TableCell>
                    <TableCell>
                      {r.reference_id ? (
                        <code
                          style={{
                            background: "#f3f4f6",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: "0.78rem",
                          }}>
                          {r.reference_id}
                        </code>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <div style={{ fontSize: "0.82rem" }}>
                        <div>{r.bank_name || "—"}</div>
                        <div style={{ color: "#6b7280", fontSize: "0.72rem" }}>
                          {r.account_number || ""}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div style={{ fontSize: "0.82rem" }}>
                        {r.admin_name || r.admin_id || "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.status || "completed"}
                        size="small"
                        color={r.status === "completed" ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(SettlementHistoryDialog);
